import { HttpStatus, IAnonymousJwtPayload } from '@bloomays-lib/types.shared';
import get from 'lodash/fp/get';

export interface IBloomaysCustomError<T = unknown> {
  extensions: {
    invalidArgs?: unknown[];
    status?: HttpStatus;
    code: string;
    meta?: T;
  };
  cause?: Error;
}

export type BloomaysCustomErrorOptions<T = unknown> = {
  status?: HttpStatus;
  code: string;
  meta?: T;
  invalidArgs?: unknown[];
  cause?: Error;
};

export class BloomaysCustomError<T = unknown> extends Error implements IBloomaysCustomError<T> {
  constructor(message: string, { status, code, meta, invalidArgs, cause }: BloomaysCustomErrorOptions<T>) {
    super(message, { cause });
    this.extensions = {
      status,
      code,
      meta,
      invalidArgs,
    };
    this.name = 'BloomaysCustomError';
    this.status = status;
    // Ensure the error prototype chain works correctly
    Object.setPrototypeOf(this, BloomaysCustomError.prototype);
  }
  extensions: IBloomaysCustomError<T>['extensions'];
  status?: HttpStatus | undefined;
  cause?: Error | undefined;
}

export const getErrorStatusCode = (err: any): number => {
  const statusCode =
    err.status ||
    err.statusCode ||
    get('extensions.status', err) ||
    get('networkError.statusCode', err) ||
    get('extensions.http.status', err);
  return statusCode ? parseInt(statusCode, 10) : 500;
};

/**
 * Returns `true` when the error was raised because a request was intentionally
 * aborted (component unmount, route change, refetch race, `AbortController`
 * timeout…). These are almost always benign — the caller cancelled the request
 * on purpose — so we don't want to surface them as Sentry incidents
 * (cf. TEC-1684, Sentry FORM-BLOOMERS-QW3 / FORM-BLOOMERS-11B).
 *
 * Matches the standard `name` set by the Fetch spec on the `DOMException`
 * raised when an associated `AbortSignal` fires, whatever surfaced it (Apollo
 * links, Auth0 SDK, plain `fetch`, unhandled rejections…).
 */
export const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  return (error as { name?: unknown }).name === 'AbortError';
};

/**
 * Returns `true` when the error reports a closed WebSocket transport under a
 * GraphQL subscription. `graphql-ws` raises `Error: Socket closed` — possibly
 * suffixed with the close event code (`Socket closed with event 1006 …`) —
 * when the connection drops mid-subscription (tab backgrounded on mobile,
 * network switch…). The subscription client re-establishes the socket on its
 * own, so reporting these to Sentry only adds transient transport noise
 * (cf. TEC-1684, Sentry FORM-BLOOMERS-WP).
 */
export const isSocketClosedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' && message.startsWith('Socket closed');
};

export class TokenExpiredError<T extends IAnonymousJwtPayload> extends BloomaysCustomError<T> {
  token: string;
  constructor(message: string, meta: T & { cause?: Error; token: string }) {
    super(message, { code: 'TOKEN_EXPIRED', status: HttpStatus.UNAUTHORIZED, meta });
    Object.defineProperty(this, 'name', { value: 'TokenExpiredError' });
    Object.setPrototypeOf(this, TokenExpiredError.prototype);
    this.token = meta.token;
  }
}

export class AuthenticationError<T = unknown> extends BloomaysCustomError<T> {
  constructor(
    message: string,
    { invalidArgs, status = HttpStatus.UNAUTHORIZED, cause, meta }: Omit<BloomaysCustomErrorOptions<T>, 'code'> = {},
  ) {
    super(message, { cause, invalidArgs, status, code: 'UNAUTHENTICATED', meta });
    Object.defineProperty(this, 'name', { value: 'AuthenticationError' });
    Object.setPrototypeOf(this, AuthenticationError.prototype);
  }
}
